import get from 'lodash/get';
import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import Button, { ButtonMode } from '../Button';
import Checkbox from '../Checkbox';
import Icon, { ICONS } from '../Icon';
import Table from '../Table';
import Tooltip from '../Tooltip';
import { DataViewItem, DataViewProps, DisabledItem, DisabledItemProps, KeyTypes, RowProps } from './DataView.types';
import styles from './DataView.module.scss';

const cx = classNames.bind(styles);

const getRenderedItem = <T extends object>(
  config: DataViewItem,
  item: T,
  row: RowProps,
  onItemClick?: (item: T, e: React.MouseEvent<HTMLElement>) => void
): ReactElement | null => {
  const {
    dataKey,
    renderWithoutDataValue,
    dataRenderer: DataRenderer,
    dataViewItemConfiguration,
    dataRendererProps,
  } = config;

  const itemContent = get(item, dataKey || '') || renderWithoutDataValue;
  if (!itemContent) {
    return null;
  }

  return DataRenderer ? (
    <DataRenderer
      value={itemContent}
      item={item}
      configuration={dataViewItemConfiguration}
      row={row}
      props={{ ...dataRendererProps, onItemClick }}
    />
  ) : (
    itemContent
  );
};

const renderSubtitle = <T extends object>(
  subtitleConfig: DataViewItem[],
  item: T,
  key: KeyTypes,
  row: RowProps,
  onItemClick?: (item: T, e: React.MouseEvent<HTMLElement>) => void
) =>
  subtitleConfig.map((itemConfig, i) => {
    const subtitleRow = { ...row, index: i };

    return getRenderedItem(itemConfig, item, subtitleRow, onItemClick);
  });

const renderDisabledItem = <T extends object>(
  disabledConfig: DataViewItem,
  disabledItemElement: DisabledItem<T, DisabledItemProps>,
  item: T,
  row: RowProps,
  onItemClick?: (item: T, e: React.MouseEvent<HTMLElement>) => void
) => {
  const { dataViewItemConfiguration, dataRendererProps } = disabledConfig;
  const DisabledItemElement = disabledItemElement;

  return (
    <DisabledItemElement
      item={item}
      configuration={dataViewItemConfiguration}
      props={{ onItemClick, ...dataRendererProps }}
      row={row}
    />
  );
};

const DataView = <T extends object>({
  getKey,
  data,
  config,
  expanded,
  classNames,
  activeKeys,
  selectable,
  searchable,
  searchByFields,
  onSelectionChange,
  bulkActionButtons,
  searchEmptyText = 'No search results',
  selectedItems = [],
  rightSideActionButtons,
}: DataViewProps<T>): ReactElement | null => {
  const { expandedViewConfig, tableViewConfig, clickable, onRowMouseUp, getIsDisabled, disabledItemElement } = config;
  const [filteredData, setFilteredData] = useState(data);
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState('');
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const onCheckboxChange = (key: KeyTypes, checked?: boolean) => {
    const updatedSelection = checked ? [...selectedItems, key] : selectedItems.filter((item) => item !== key);

    onSelectionChange?.(updatedSelection);
  };

  const onCheckAll = (checked?: boolean) => {
    const updatedSelection = checked ? filteredData.map((item) => getKey(item)) : [];

    onSelectionChange?.(updatedSelection);
  };

  const allChecked = useMemo(
    () => selectedItems?.length > 0 && isEqual(sortBy(selectedItems), sortBy(filteredData.map(getKey))),
    [selectedItems, filteredData]
  );

  const shouldRenderSearch = () => searchable && !selectedItems?.length;

  const focusInput = () => {
    if (document.activeElement !== inputRef?.current) {
      inputRef?.current?.focus();
    }
  };

  const onSearchButtonClick = () => {
    setShowSearch(true);
    focusInput();
  };

  const onResetButtonClick = () => {
    setSearchValue('');
    focusInput();
  };

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  useEffect(() => {
    if (!searchByFields?.length) {
      return;
    }

    if (!searchValue) {
      setFilteredData(data);
    } else {
      const filteredItems = data.filter((item) =>
        searchByFields.some((field) => String(item[field])?.toUpperCase().includes(searchValue.toUpperCase()))
      );

      setFilteredData(filteredItems);
    }
  }, [searchValue, searchByFields]);

  if ((!expanded || !expandedViewConfig) && tableViewConfig) {
    return (
      <Table
        data={data}
        getKey={getKey}
        tableViewConfig={tableViewConfig}
        onRowMouseUp={onRowMouseUp}
        activeKeys={activeKeys}
        getIsDisabled={getIsDisabled}
        disabledItemElement={disabledItemElement}
      />
    );
  }

  if ((expanded || !tableViewConfig) && expandedViewConfig) {
    const {
      title: titleConfig,
      subtitle: subtitleConfig,
      content: contentConfig,
      disabled: disabledConfig,
      contentIndent,
    } = expandedViewConfig;
    return (
      <div className={cx('body')} data-testid="expanded-view">
        {(selectable || searchable) && data.length > 0 && (
          <div className={cx('toolbar')} data-testid="data-view-toolbar">
            {selectable && (
              <div className={cx('select-all')}>
                <Checkbox
                  dataTestId="data-view-select-all"
                  checked={allChecked}
                  disabled={!filteredData?.length}
                  indeterminate={!allChecked && selectedItems.length > 0}
                  onChange={(_, data) => onCheckAll(data.checked)}
                />
              </div>
            )}
            {shouldRenderSearch() && (
              <div className={cx('search-wrapper')}>
                <Tooltip
                  trigger={
                    <Button
                      mode={ButtonMode.TERTIARY}
                      onClick={onSearchButtonClick}
                      dataTestId="search-button"
                      ariaLabel="Search"
                      hasEqualSidesPadding
                    >
                      <Icon name={ICONS.Search} dataTestId="search-button-icon" />
                    </Button>
                  }
                  content="Search"
                />
                {showSearch && (
                  <>
                    <input
                      className={cx('search-input')}
                      autoFocus
                      value={searchValue}
                      onChange={(event) => setSearchValue(event.target.value)}
                      ref={inputRef}
                    />
                    {searchValue.length > 0 && (
                      <Tooltip
                        trigger={
                          <Button
                            className={cx('search-clear')}
                            dataTestId="search-clear-button"
                            mode={ButtonMode.TERTIARY}
                            onClick={onResetButtonClick}
                          >
                            <Icon name={ICONS.Clear} width={12} height={12} />
                          </Button>
                        }
                        content="Reset"
                      />
                    )}
                  </>
                )}
              </div>
            )}
            {selectedItems?.length > 0 && (
              <div className={cx('bulk-actions')} data-testid="data-view-action-buttons">
                {bulkActionButtons}
              </div>
            )}
            {rightSideActionButtons && (
              <div data-testid="data-view-right-side-action-buttons">{rightSideActionButtons}</div>
            )}
          </div>
        )}
        {searchable && searchValue && data.length > 0 && !filteredData?.length ? (
          <p className={cx('search-empty')}>{searchEmptyText} </p>
        ) : (
          filteredData?.length && (
            <div className={cx('data-items-wrapper')}>
              {filteredData.map((item, index) => {
                const key = getKey(item);
                const row: RowProps = {
                  index,
                  isDisabled: getIsDisabled?.(item),
                  isHovered: hoveredRowIndex === index,
                  isIndent: contentIndent,
                };

                return (
                  // eslint-disable-next-line jsx-a11y/interactive-supports-focus
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${key}-${index}`}
                    role="row"
                    className={cx('data-item', classNames?.dataItemContent, {
                      'is-active': activeKeys?.includes(key),
                      selectable,
                      clickable: clickable || !!onRowMouseUp,
                      'is-disabled': row.isDisabled,
                    })}
                    onMouseEnter={() => setHoveredRowIndex(index)}
                    onMouseLeave={() => setHoveredRowIndex(null)}
                    data-testid="expanded-view-item"
                  >
                    <div className={cx('header')}>
                      {selectable && (
                        <div className={cx('checkbox-wrapper')}>
                          <Checkbox
                            dataTestId="data-item-checkbox"
                            checked={selectedItems.includes(key)}
                            onChange={(_, data) => onCheckboxChange(key, data.checked)}
                            onMouseUp={(e) => e.stopPropagation()}
                          />
                        </div>
                      )}
                      {titleConfig && (
                        <div className={cx('title')} data-testid="expanded-view-title">
                          {getRenderedItem(titleConfig, item, row, onRowMouseUp)}
                        </div>
                      )}
                    </div>
                    {contentConfig && (
                      <div className={cx('content')} data-testid="expanded-view-content">
                        {getRenderedItem(contentConfig, item, row, onRowMouseUp)}
                      </div>
                    )}
                    {subtitleConfig && (
                      <div className={cx('subtitle', { indent: contentIndent })} data-testid="expanded-view-subtitle">
                        <div className={cx('subtitle-elements')}>
                          {renderSubtitle(subtitleConfig, item, key, row, onRowMouseUp)}
                        </div>
                      </div>
                    )}
                    {row.isDisabled && row.isHovered && disabledItemElement && disabledConfig && (
                      <div className={cx('disabled-item-element')}>
                        {renderDisabledItem(disabledConfig, disabledItemElement, item, row, onRowMouseUp)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>
    );
  }

  return null;
};

export default DataView;
